import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Badge,
  Pagination,
  InputGroup,
  Card,
  CardBody,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import AppConstants from "../../AppConstants";
import "./index.scss";

interface APIUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string;
  profilePicture?: string;
  profileImage?: string;
  registrationDate: string;
  lastLoginDate: string;
  status: string;
  subscriptionStatus: string;
  isBlocked: boolean;
  subscription?: {
    plan: string;
    maxUsers: number;
    currentUsers: number;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
  };
  subscriptions?: Array<{
    stripeSubscriptionId: string;
    role: string;
    active: boolean;
    _id: string;
  }>;
  subscriptionRole: string | null;
  assignedUsers: string[];
  inspectionCount: number;
  address?: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
}

interface APIResponse {
  success: boolean;
  message: string;
  data: {
    users: APIUser[];
    statistics: {
      totalUsers: number;
      activeUsers: number;
      inactiveUsers: number;
      blockedUsers: number;
      totalSubscriptions: number;
      activeSubscriptions: number;
      totalInspections: number;
      recentRegistrations: number;
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<APIUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [statistics, setStatistics] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [isApiInProgress, setIsApiInProgress] = useState<boolean>(false);

  const navigate = useNavigate();

  // Helper function to get profile image
  const getProfileImage = (user: APIUser): string => {
    if (user.profilePicture) return user.profilePicture;
    if (user.profileImage) return user.profileImage;
    // Generate dummy avatar using UI Avatars
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      user.fullName
    )}&background=random&color=fff&size=128`;
  };

  // Format date helper
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  };

  // Format US phone number as (XXX)-XXX-XXXX
  function formatUSPhone(phone: string): string {
    const cleaned = ("" + phone).replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone;
  }

  // Helper function to get subscription expiration date
  const getSubscriptionExpiryDate = (user: APIUser): string | undefined => {
    // First try to get from subscription object
    if (user.subscription?.currentPeriodEnd) {
      return user.subscription.currentPeriodEnd;
    }

    // If no subscription object but user has active subscription in subscriptions array
    // For members, we might not have period dates, so return undefined to show N/A
    return undefined;
  };

  const getStatusVariant = (status: string): string => {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "danger";
      case "deleted":
        return "danger";
      case "blocked":
        return "danger";
      default:
        return "danger";
    }
  };

  // Helper function to get display text for status
  const getStatusDisplayText = (status: string): string => {
    switch (status.toLowerCase()) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      case "deleted":
        return "Deleted";
      case "blocked":
        return "Blocked";
      default:
        return status;
    }
  };

  // Get auth token from localStorage or your auth context
  const getAuthToken = () => {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken")
    );
  };

  const fetchUsers = async (
    page: number = 1,
    status: string = "all",
    search: string = ""
  ) => {
    // Prevent multiple simultaneous API calls
    if (isApiInProgress) {
      return;
    }

    setIsApiInProgress(true);
    setLoading(true);
    try {
      let url = `${AppConstants.ADMIN_USER_MANAGEMENT_API}?page=${page}&limit=${itemsPerPage}`;

      // Handle status parameter
      if (status === "all") {
        url += `&status=all`;
      } else if (status && status !== "all") {
        url += `&status=${status}`;
      }

      // Add search parameter if provided
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const token = getAuthToken();

      const headers: HeadersInit = {
        "Content-Type": "application/json"
      };

      // Add authorization header if token exists
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Authentication required. Please log in again.");
          // Redirect to login page or handle auth failure
          navigate("/login");
          return;
        }
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `Failed to fetch users: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data: APIResponse = await response.json();

      if (data.success) {
        setUsers(data.data.users);
        setStatistics(data.data.statistics);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message || "Failed to load users");
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
      setIsApiInProgress(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Reset to page 1 when search term or status changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  // Initial load and main effect for fetching data
  useEffect(() => {
    fetchUsers(currentPage, statusFilter, debouncedSearchTerm);
  }, [currentPage, statusFilter, debouncedSearchTerm]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle status filter change - Updated to not call fetchUsers directly
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    // fetchUsers will be called by the useEffect
  };

  // Clear search when changing status if no results
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const items = [];
    const totalPages = pagination.totalPages;
    const current = pagination.currentPage;

    // First page
    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={current === 1}
      />
    );

    // Previous page
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, current - 1))}
        disabled={current === 1}
      />
    );

    // Page numbers (show current page and 2 pages before/after)
    const startPage = Math.max(1, current - 2);
    const endPage = Math.min(totalPages, current + 2);

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === current}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Next page
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, current + 1))}
        disabled={current === totalPages}
      />
    );

    // Last page
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={current === totalPages}
      />
    );

    return items;
  };

  const handleUserClick = (userId: string) => {
    navigate(`/user-management/${userId}`);
  };

  const handleBlockUser = async (userId: string, isBlocked: boolean) => {
    try {
      // API call to block/unblock user would go here
      // For now, we'll just update the local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? { ...user, status: isBlocked ? "blocked" : "active", isBlocked }
            : user
        )
      );

      toast.success(
        isBlocked ? "User blocked successfully" : "User unblocked successfully"
      );
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  return (
    <Container fluid className="p-0 user-management mb-5">
      {/* Filter and Search */}
      <Card className="filter-block p-1 mb-3 rounded shadow-sm border-0">
        <CardBody>
          <Row className="justify-content-between">
            <Col lg={4} md={12} className="mb-3 mb-lg-0 filters">
              <InputGroup>
                <Form.Control
                  placeholder="Search by name, email, or phone"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchTerm !== debouncedSearchTerm && (
                  <small className="text-muted mt-1 d-block">
                    Searching...
                  </small>
                )}
              </InputGroup>
            </Col>
            <Col lg={8} md={12} className="filters">
              <div className="d-flex justify-content-end search-create">
                <div className="d-flex align-items-center me-0 me-md-3">
                  <span className="me-2" style={{ minWidth: "60px" }}>
                    Filter by
                  </span>
                  <Form.Select
                    style={{ width: "auto", minWidth: "230px" }}
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="deleted">Deleted</option>
                    <option value="blocked">Blocked</option>
                  </Form.Select>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {loading ? (
        <Loader text="Loading users..." />
      ) : (
        <>
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th>Name</th>
                  <th>Account</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Registered on</th>
                  <th>Expires on</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => handleUserClick(user.id)}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="avatar me-2">
                            <img
                              src={getProfileImage(user)}
                              alt={user.fullName}
                              width="36"
                              height="36"
                              className="rounded-circle"
                              onError={(e) => {
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  user.fullName
                                )}&background=random&color=fff&size=128`;
                              }}
                            />
                          </div>
                          <span className="gilroy-semibold">
                            {user.fullName.length > 20 ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    {user.fullName}
                                  </Tooltip>
                                }
                              >
                                <span style={{ cursor: 'pointer' }}>
                                  {user.fullName.substring(0, 20)}...
                                </span>
                              </OverlayTrigger>
                            ) : (
                              user.fullName
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-capitalize">
                          {user.subscriptionRole || "Member"}
                        </span>
                      </td>
                      <td>
                        {user.email.length > 20 ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {user.email}
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }}>
                              {user.email.substring(0, 20)}...
                            </span>
                          </OverlayTrigger>
                        ) : (
                          user.email
                        )}
                      </td>
                      <td>
                        {user.phoneNumber
                          ? formatUSPhone(user.phoneNumber)
                          : "N/A"}
                      </td>
                      <td>{formatDate(user.registrationDate)}</td>
                      <td>{formatDate(getSubscriptionExpiryDate(user))}</td>
                      <td>
                        <Badge
                          bg={getStatusVariant(
                            user.subscriptionStatus || user.status
                          )}
                          className="px-3 py-2 text-capitalize"
                        >
                          {getStatusDisplayText(
                            user.subscriptionStatus || user.status
                          )}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      {debouncedSearchTerm
                        ? `No users found matching "${debouncedSearchTerm}"${
                            statusFilter !== "all"
                              ? ` with status "${statusFilter}"`
                              : ""
                          }`
                        : statusFilter !== "all"
                        ? `No ${statusFilter} users found`
                        : "No users found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="d-flex justify-content-between align-items-center mt-2 pagination-block">
              <div>
                Showing: {(pagination.currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  pagination.currentPage * itemsPerPage,
                  pagination.totalItems
                )}{" "}
                of {pagination.totalItems}
              </div>
              {pagination.totalPages > 1 && (
                <Pagination className="mb-0">
                  {renderPaginationItems()}
                </Pagination>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default UserManagement;
